import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CitedTopic, CitationRef } from '../../../lib/commentCitations';
import { FileText } from 'lucide-react';
import DocViewerPanel from './DocViewerPanel';

interface CitedTopicsViewerProps {
  docket: string;
  topics: CitedTopic[];
  /** Storage folders to resolve source PDFs from (comment vs reply round). */
  segments?: string[];
  /** Citation id to open on mount (deep link). Applied once when topics load. */
  initialCitationId?: string;
  /** Notified whenever the active citation changes (for URL deep-linking). */
  onActiveChange?: (citation: CitationRef | null) => void;
}

/** Presentational split view: cited points on the left (each with a clickable
 * source citation), a pdf.js document viewer on the right. Data-source
 * agnostic — callers pass already-parsed topics (from storage or an in-memory
 * payload). */
const CitedTopicsViewer: React.FC<CitedTopicsViewerProps> = ({
  docket,
  topics,
  segments,
  initialCitationId,
  onActiveChange,
}) => {
  const [active, setActive] = useState<CitationRef | null>(null);
  // The chip that opened the viewer, so focus can return to it on close.
  const lastChipRef = useRef<HTMLButtonElement | null>(null);

  const idMap = useMemo(() => {
    const m = new Map<string, CitationRef>();
    topics.forEach((t) => t.entities.forEach((e) => e.points.forEach((p) => m.set(p.id, p))));
    return m;
  }, [topics]);

  const pointCount = useMemo(
    () => topics.reduce((n, t) => n + t.entities.reduce((m, e) => m + e.points.length, 0), 0),
    [topics],
  );

  // Apply a deep-linked citation once, when the points are available.
  const initialIdRef = useRef(initialCitationId);
  const didApply = useRef(false);
  useEffect(() => {
    if (didApply.current || idMap.size === 0) return;
    didApply.current = true;
    const id = initialIdRef.current;
    if (id && idMap.has(id)) setActive(idMap.get(id)!);
  }, [idMap]);

  // Report active changes to the parent for URL syncing, skipping the
  // initial mount so we don't clobber an incoming deep link.
  const cbRef = useRef(onActiveChange);
  cbRef.current = onActiveChange;
  const firstNotify = useRef(true);
  useEffect(() => {
    if (firstNotify.current) {
      firstNotify.current = false;
      return;
    }
    cbRef.current?.(active);
  }, [active]);

  if (!pointCount) {
    return <div className="py-6 text-sm text-gray-500">No sourced points available for this summary.</div>;
  }

  return (
    <div className="flex h-[min(80vh,760px)] overflow-hidden rounded-lg border border-gray-200">
      {/* Left: sourced points */}
      <div className={`overflow-auto p-4 ${active ? 'hidden md:block md:w-[52%]' : 'w-full'}`}>
        <p className="mb-3 text-xs text-gray-500">
          {pointCount} sourced points · click a citation to see the supporting text in the document
        </p>
        {topics.map((t) => (
          <div key={t.topic} className="mb-5">
            <h4 className="mb-2 text-sm font-semibold text-gray-900">{t.topic}</h4>
            <div className="space-y-2.5">
              {t.entities.map((ent) =>
                ent.points.map((p) => {
                  const isActive = active?.id === p.id;
                  return (
                    <div
                      key={p.id}
                      className={`rounded-md border p-3 text-sm transition-colors ${
                        isActive
                          ? 'border-emerald-300 bg-emerald-50/60'
                          : 'border-gray-200 bg-white hover:border-emerald-200'
                      }`}
                    >
                      <div className="mb-1 text-xs font-medium text-gray-500">{ent.entity}</div>
                      <p className="leading-relaxed text-gray-800">{p.point}</p>
                      {p.sourceText && (
                        <button
                          onClick={(e) => {
                            lastChipRef.current = e.currentTarget;
                            setActive(p);
                          }}
                          aria-label={`View source document for ${ent.entity}${
                            p.page ? `, page ${p.page}` : ''
                          }`}
                          className="mt-2 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                        >
                          <FileText className="h-3 w-3" aria-hidden="true" />
                          {p.page ? `Source · p.${p.page}` : 'Source'}
                        </button>
                      )}
                    </div>
                  );
                }),
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Right: document viewer (docked on md+, full-width on mobile) */}
      {active && (
        <div className="w-full md:w-[48%]">
          <DocViewerPanel
            docket={docket}
            citation={active}
            segments={segments}
            onClose={() => {
              setActive(null);
              // Return focus to the chip that opened the viewer.
              lastChipRef.current?.focus();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CitedTopicsViewer;
